import { action, computed, makeObservable, observable, reaction, runInAction, toJS } from "mobx";
import { AppSubStore } from "@/store/types";
import type { AppSubStoreArgs } from "@/store/types";
import type { ICalendarStore } from "@/store/calendar/types";
import { AppStoreCalendarGoogleStore } from "@/store/calendar/google/calendar-google.store";
import { AppStoreCalendarOutlookStore } from "@/store/calendar/outlook/calendar-outlook.store";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { electronModule } from "@/modules/electron";
import { CalendarEvent } from "@/modules/electron/types";
import { api } from "@/modules/api";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { liveQuery, Subscription } from "dexie";
import { DateTime } from "luxon";
import type { ConnectedCalendarEventListResponse } from "@/store/calendar/types";
import { IndexedSpaceAccountCalendarEventSyncUpdateValue } from "@/store/calendar/space-account-calendar-event/types";
import { getOngoingAndUpcomingCalendarEvents } from "@/store/calendar/space-account-calendar-event/search-calendar-events";

export class AppStoreCalendarStore extends AppSubStore implements ICalendarStore {
  google: AppStoreCalendarGoogleStore;
  outlook: AppStoreCalendarOutlookStore;

  fetchingCalendarEvents = false;
  calendarEventsSubscription?: Subscription;
  queryStartTime: DateTime;
  ongoingEvents: CalendarEvent[] = [];
  upcomingEvents: CalendarEvent[] = [];
  refreshingCalendarIds: Set<string> = new Set();

  constructor(args: AppSubStoreArgs) {
    super(args);

    this.google = new AppStoreCalendarGoogleStore(args);
    this.outlook = new AppStoreCalendarOutlookStore(args);

    this.queryStartTime = DateTime.now().startOf("minute");

    makeObservable<
      this,
      "fetchingCalendarEvents" | "fetchUpcomingMeetingsFromStore" | "subscribeToCalendarEvents"
    >(this, {
      useInitializeCalendarEffects: false,
      google: observable,
      outlook: observable,
      fetchingCalendarEvents: observable,
      calendarEventsSubscription: observable,
      queryStartTime: observable,
      ongoingEvents: observable,
      upcomingEvents: observable,
      refreshingCalendarIds: observable,
      resetState: action,
      refreshUpcomingMeetings: action,
      isFetchingUpcomingMeetings: computed,
      fetchUpcomingMeetingsFromStore: action,
      subscribeToCalendarEvents: false,
      refreshCalendar: action,
    });
  }

  async resetState() {
    this.google.resetState();
    this.outlook.resetState();
    this.fetchingCalendarEvents = false;
    this.calendarEventsSubscription?.unsubscribe();
    this.ongoingEvents = [];
    this.upcomingEvents = [];
    this.queryStartTime = DateTime.now().startOf("minute");
    this.refreshingCalendarIds = new Set();
  }

  get isFetchingUpcomingMeetings() {
    return this.fetchingCalendarEvents;
  }

  async refreshUpcomingMeetings() {
    if (this.fetchingCalendarEvents) return;
    if (!this.google.isConnected && !this.outlook.isConnected) {
      runInAction(() => {
        this.google.upcomingMeetings = [];
        this.outlook.upcomingMeetings = [];
      });
      return;
    }

    runInAction(() => {
      this.fetchingCalendarEvents = true;
    });

    try {
      await api.post("/v2/connected-calendars/reconcile", {});
      await this.fetchUpcomingMeetingsFromStore();
    } catch (unknownError) {
      logger.error({
        message: "[AppStoreCalendarStore] Failed to reconcile Upcoming meetings",
        info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
      });
    } finally {
      runInAction(() => {
        this.fetchingCalendarEvents = false;
      });
    }
  }

  async fetchUpcomingMeetingsFromStore() {
    if (!this.google.isConnected && !this.outlook.isConnected) {
      runInAction(() => {
        this.google.upcomingMeetings = [];
        this.outlook.upcomingMeetings = [];
      });
      return;
    }

    try {
      const response = await api.get("/v2/connected-calendar-events", {
        params: {
          query: {
            limit: 50,
          },
        },
      });

      const data = response.data as ConnectedCalendarEventListResponse | undefined;
      if (!data) {
        throw new Error("Missing connected calendar events payload.");
      }

      // Filter events by provider and update respective stores
      const googleEvents = data.results.filter(event => event.provider_kind === "GOOGLE_CALENDAR");
      const outlookEvents = data.results.filter(
        event => event.provider_kind === "OUTLOOK_CALENDAR"
      );

      if (this.google.isConnected) {
        this.google.setUpcomingMeetingsFromEvents(googleEvents);
      } else {
        runInAction(() => {
          this.google.upcomingMeetings = [];
        });
      }

      if (this.outlook.isConnected) {
        this.outlook.setUpcomingMeetingsFromEvents(outlookEvents);
      } else {
        runInAction(() => {
          this.outlook.upcomingMeetings = [];
        });
      }
    } catch (unknownError) {
      logger.error({
        message: "[AppStoreCalendarStore] Failed to load stored upcoming meetings",
        info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
      });
    }
  }

  async refreshCalendar(
    connectedCalendarId: string,
    { runSync = true }: { runSync?: boolean } = {}
  ) {
    if (this.refreshingCalendarIds.has(connectedCalendarId)) return;

    runInAction(() => {
      this.refreshingCalendarIds = new Set([...this.refreshingCalendarIds, connectedCalendarId]);
    });

    try {
      await api.post("/v2/connected-calendars/{calendar_id}/refresh", {
        params: {
          path: {
            calendar_id: connectedCalendarId,
          },
        },
        body: {
          run_sync: runSync,
        },
      });
    } catch (unknownError) {
      logger.error({
        message: "[AppStoreCalendarStore] Failed to refresh calendar",
        info: {
          connectedCalendarId,
          error: objectModule.safeErrorAsJson(unknownError as Error),
        },
      });
    } finally {
      runInAction(() => {
        const newSet = new Set(this.refreshingCalendarIds);
        newSet.delete(connectedCalendarId);
        this.refreshingCalendarIds = newSet;
      });
    }
  }

  private subscribeToCalendarEvents() {
    this.calendarEventsSubscription?.unsubscribe();

    const getCalendarEventsByIds = async (eventIds: string[]) => {
      const eventRecords = (
        await this.store.spaceAccountCalendarEvents.localTable.bulkGet(eventIds)
      ).filter((id): id is IndexedSpaceAccountCalendarEventSyncUpdateValue => Boolean(id));

      const events: CalendarEvent[] = [];

      const spaceAccountCalendars = await this.store.spaceAccountCalendars.localTable.bulkGet(
        eventRecords
          .map(calendar => calendar.model_data?.associated_space_account_calendar_id)
          .filter((id): id is string => Boolean(id))
      );

      const associatedSpaceAccountCalendarConnections =
        await this.store.spaceAccountCalendarConnections.localTable.bulkGet(
          spaceAccountCalendars
            .map(event => event?.model_data?.associated_space_account_calendar_connection_id)
            .filter((id): id is string => Boolean(id))
        );

      for (const eventRecord of eventRecords) {
        const eventId = eventRecord.model_id;
        const associatedSpaceAccountCalendarId =
          eventRecord.model_data?.associated_space_account_calendar_id;
        if (!associatedSpaceAccountCalendarId || !eventRecord.model_data) continue;

        const calendar = spaceAccountCalendars.find(
          calendar => calendar?.model_id === associatedSpaceAccountCalendarId
        );
        if (!calendar || !calendar.model_data) {
          logger.warn({
            message: "[AppStoreCalendarStore] Calendar missing",
            info: { eventRecord },
          });
          continue;
        }
        const connection = associatedSpaceAccountCalendarConnections.find(
          connection =>
            connection?.model_id ===
            calendar.model_data.associated_space_account_calendar_connection_id
        );
        if (!connection) {
          logger.warn({
            message: "[AppStoreCalendarStore] Connection missing",
            info: { eventRecord, calendar },
          });
          continue;
        }

        const providerKind = connection.model_data?.provider_kind;
        const eventIdValue = associatedSpaceAccountCalendarId || eventId;

        if (providerKind === "GOOGLE_CALENDAR") {
          events.push({
            id: eventIdValue,
            calendarEventId: eventId,
            title: eventRecord.model_data.primary_label ?? undefined,
            url: eventRecord.model_data.meet_link ?? undefined,
            platform: "google-meet",
            startTime: eventRecord.model_data.start_time ?? undefined,
            endTime: eventRecord.model_data.end_time ?? undefined,
            attendees: [],
          });
        } else if (providerKind === "OUTLOOK_CALENDAR") {
          events.push({
            id: eventIdValue,
            calendarEventId: eventId,
            title: eventRecord.model_data.primary_label ?? undefined,
            url: eventRecord.model_data.meet_link ?? undefined,
            platform: "teams",
            startTime: eventRecord.model_data.start_time ?? undefined,
            endTime: eventRecord.model_data.end_time ?? undefined,
            attendees: [],
          });
        }
      }

      return events;
    };

    this.calendarEventsSubscription = liveQuery(async () => {
      const startTime = this.queryStartTime;

      const { ongoingEventIds, upcomingEventIds } = await getOngoingAndUpcomingCalendarEvents({
        startTime,
        store: this.store,
      });

      const [ongoingEvents, upcomingEvents] = await Promise.all([
        getCalendarEventsByIds(ongoingEventIds),
        getCalendarEventsByIds(upcomingEventIds),
      ]);

      return { ongoingEvents, upcomingEvents };
    }).subscribe({
      next: ({ ongoingEvents: ongoingEvents, upcomingEvents: upcomingEvents }) => {
        logger.debug({
          message: "[AppStoreCalendarStore] Calendar events updated",
          info: objectModule.safeAsJson({
            ongoingEvents: ongoingEvents.map(event => toJS(event)),
            upcomingEvents: upcomingEvents.map(event => toJS(event)),
          }),
        });
        runInAction(() => {
          this.ongoingEvents = ongoingEvents;
          this.upcomingEvents = upcomingEvents;
        });
      },
    });
  }

  useInitializeCalendarEffects() {
    /**
     * Subscribe to calendar events and forward changes to the desktop app.
     */
    useEffectOnMount(() => {
      // Subscribe to calendar events from local database
      this.subscribeToCalendarEvents();

      // Update queryStartTime exactly on minute marks (:00)
      const abortController = new AbortController();

      const keepUpdatingUpcommingMeetingsStart = () => {
        if (abortController.signal.aborted) return;

        setTimeout(
          keepUpdatingUpcommingMeetingsStart,
          // Time to the next minute mark.
          DateTime.now().endOf("minute").diffNow().milliseconds
        );

        runInAction(() => {
          this.queryStartTime = DateTime.now().startOf("minute");
        });
      };

      keepUpdatingUpcommingMeetingsStart();

      // Resubscribe to calendar events when currentDayStart changes
      // (since liveQuery doesn't track MobX observables)
      const dayChangeRelease = reaction(
        () => this.queryStartTime,
        () => {
          this.subscribeToCalendarEvents();
        }
      );

      // If it's web rather than desktop there's no problem,
      // we need to observe upcomingMeetings either way.
      const release = reaction(
        () => ({
          ongoing: this.ongoingEvents,
          upcoming: this.upcomingEvents,
        }),
        ({ ongoing, upcoming }) => {
          // Don't send mobx objects to the desktop app.
          const ongoingEvents = ongoing.map(event => toJS(event));
          const upcomingEvents = upcoming.map(event => toJS(event));
          logger.debug({
            message: "[AppStoreCalendarStore] Upcoming events updated",
            info: objectModule.safeAsJson({
              ongoingEvents,
              upcomingEvents,
            }),
          });
          electronModule.setCalendarEvents({ ongoingEvents, upcomingEvents });
        }
      );

      return () => {
        abortController.abort();
        dayChangeRelease();
        release();
        this.calendarEventsSubscription?.unsubscribe();
        electronModule.setCalendarEvents({ ongoingEvents: [], upcomingEvents: [] });
      };
    });
  }
}
